import { GenericDialog, GenericSuccessDialog, SuccessDialogContentProps } from '@components/display/dialogs';
import { toastProps } from '@components/display/toasts';
import { HttpStateWrapper } from '@components/layout';
import { usePublicLayoutOptions } from '@components/layout/public';
import { APP_PREFIX_PATH } from '@constants/common';
import { TemporaryRegistrationBanner } from '@event/components/display/banners';
import { LearnMoreDialog } from '@event/components/display/dialogs';
import { COLLEAGUE_HASH_KEY, GUEST_ACCESS_HASH_KEY } from '@event/constants';
import { PublicEventContext } from '@event/contexts/PublicEventContext';
import { usePublicEvent } from '@event/hooks/useEvent';
import { useEventDetailScreenHandler } from '@event/hooks/useEventDetailScreenHandler';
import { CredentialSelect, EventDetailCardView } from '@event/routes/public';
import {
    useJoinWaitingListMutation,
    useLeaveWaitingListMutation,
    useRegisterUserMutation,
    useUnregisterUserMutation,
} from '@event/services/events';
import { EventTypes } from '@event/types/event';
import { withEventTypeCheck } from '@event/utils';
import { getOppositeEventType } from '@event/utils/event';
import { useAppDispatch } from '@hooks/generic';
import { useAuth } from '@hooks/useAuth';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Typography } from '@mui/material';
import { useGetUserByGuestHashQuery } from '@services/accounts';
import { setTitle } from '@store/appSlice';
import { setBreadcrumbs } from '@store/breadcrumbSlice';
import { setError } from '@store/errorSlice';
import { handleError } from '@utils/common';
import { getMatchCase, isApiResponseError } from '@utils/service';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';

const EventDetail = withEventTypeCheck(() => {
    const dispatch = useAppDispatch();
    const { type, eventId } = useParams();
    const [searchParams] = useSearchParams();
    const eventType = type as EventTypes;
    const { isLoggedIn, isUserLoading, user } = useAuth();
    const [waitingListInviteDialogOpen, setWaitingListInviteDialogOpen] = useState(false);
    const intl = useIntl();
    const [showTemporaryRegistrationBanner, setShowTemporaryRegistrationBanner] = useState(false);
    const [temporaryRegistrationDialogOpen, setTemporaryRegistrationDialogOpen] = useState(false);
    const [waitingListSuccessState, setWaitingListSuccessState] =
        useState<Pick<SuccessDialogContentProps, 'description' | 'title' | 'icon'>>();
    const [waitingListSuccessDialogOpen, setWaitingListSuccessDialogOpen] = useState(false);
    const [waitingListActionSuccessDialogState, setWaitingListActionSuccessDialogState] = useState<
        'none' | 'join' | 'leave'
    >('none');
    const [hasRespondedToInvitation, setHasRespondedToInvitation] = useState(false);

    const colleagueHash = useMemo(() => searchParams.get(COLLEAGUE_HASH_KEY), [searchParams]);
    const guestAccessHashParam = useMemo(() => searchParams.get(GUEST_ACCESS_HASH_KEY), [searchParams]);

    const { event, isEventLoading, isEventFetching, eventError } = usePublicEvent({
        eventType,
        eventId: Number(eventId),
        colleagueHash,
        guestAccessHash: guestAccessHashParam,
    });

    const showWaitingListDialog = useMemo(
        () =>
            event?.enable_waiting_list &&
            event?.is_user_in_waiting_list &&
            event?.user_has_waiting_list_invitation &&
            !hasRespondedToInvitation,
        [
            event?.enable_waiting_list,
            event?.is_user_in_waiting_list,
            event?.user_has_waiting_list_invitation,
            hasRespondedToInvitation,
        ]
    );

    const {
        event: oppositeEvent,
        isEventLoading: isOppositeEventLoading,
        isEventFetching: isOppositeEventFetching,
    } = usePublicEvent({
        eventType: getOppositeEventType(eventType),
        eventId: eventType.includes('event')
            ? Number(event?.hybrid_event?.online_event?.id)
            : Number(event?.hybrid_event?.onsite_event?.id),
        colleagueHash,
        guestAccessHash: guestAccessHashParam,
        params: {
            skip: !event || isEventLoading || isEventFetching || !!eventError || !event.hybrid_event,
        },
    });

    const { data: guestUserInfo, isFetching: isGuestUserInfoFetching } = useGetUserByGuestHashQuery(
        {
            guest_access_hash: guestAccessHashParam as string, // We can safely assume so because the call will be skipped if it's not truthy
        },
        {
            skip: !guestAccessHashParam,
            selectFromResult: (result) => ({
                ...result,
                data: guestAccessHashParam ? result.data : undefined,
            }),
        }
    );

    const [registerUser, { isLoading: isRegisterLoading }] = useRegisterUserMutation();
    const [unregisterUser, { isLoading: isUnregisterLoading }] = useUnregisterUserMutation();
    const [leaveWaitingList, { isLoading: isLeavingWaitingList }] = useLeaveWaitingListMutation();
    const [joinWaitingList, { isLoading: isJoiningWaitingList }] = useJoinWaitingListMutation();

    const { screen, showGuestAccessBanner, setShowGuestAccessBanner } = useEventDetailScreenHandler({
        isLoading:
            isRegisterLoading ||
            isUnregisterLoading ||
            isEventLoading ||
            isLeavingWaitingList ||
            isJoiningWaitingList ||
            isUserLoading ||
            isGuestUserInfoFetching,
        guestAccessHash: guestAccessHashParam,
        isLoggedIn,
        event,
        user,
    });
    const isPageLoading = useMemo(
        () =>
            isUserLoading ||
            isEventLoading ||
            isGuestUserInfoFetching ||
            isJoiningWaitingList ||
            isLeavingWaitingList ||
            !screen,
        [isUserLoading, isEventLoading, isGuestUserInfoFetching, isJoiningWaitingList, isLeavingWaitingList, screen]
    );

    useEffect(() => {
        dispatch(
            setBreadcrumbs([
                {
                    label: intl.formatMessage({ id: 'navbar.events', defaultMessage: 'Events' }),
                    to: `${APP_PREFIX_PATH}/events`,
                },
                {
                    label: event?.name
                        ? event.name
                        : intl.formatMessage({
                              id: eventError ? 'notFound' : 'loading',
                              defaultMessage: eventError ? 'Not Found' : 'Loading',
                          }),
                    to: `${APP_PREFIX_PATH}/events/${eventType}/${eventId}`,
                    loading: isEventLoading,
                },
            ])
        );
    }, [dispatch, event?.name, eventError, eventId, eventType, intl]);

    useEffect(() => {
        if (!isPageLoading) {
            dispatch(
                setTitle(
                    event?.name
                        ? event?.name
                        : intl.formatMessage({ id: 'event.browserTitle', defaultMessage: 'Event Manager' })
                )
            );
        }
    }, [dispatch, event?.name, isPageLoading, intl]);

    useEffect(() => {
        if (!isPageLoading) {
            if (eventError && isApiResponseError(eventError) && eventError.status === 403) {
                dispatch(setError({ status: isLoggedIn ? 403 : 401, case: getMatchCase() }));
            }
        }
    }, [dispatch, eventError, isLoggedIn, isPageLoading]);

    useEffect(() => {
        if (showWaitingListDialog && !isPageLoading) {
            setWaitingListInviteDialogOpen(true);
        }
    }, [showWaitingListDialog, isPageLoading]);

    useEffect(() => {
        if (
            !event?.user_meets_level_badge_requirements?.meets_requirements_without_temp_badges &&
            !!event?.user_meets_level_badge_requirements?.meets_requirements &&
            event?.allow_temporary_registration
        ) {
            setShowTemporaryRegistrationBanner(true);
        } else {
            setShowTemporaryRegistrationBanner(false);
        }
    }, [event]);

    const handleTemporaryRegistrationDialogOpen = useCallback(() => {
        setTemporaryRegistrationDialogOpen(true);
    }, []);

    const handleTemporaryRegistrationDialogClose = () => {
        setTemporaryRegistrationDialogOpen(false);
    };

    const fullWidthTopContent = useMemo(
        () => (
            <>
                {showGuestAccessBanner ? (
                    <div className="min-h-[50px] bg-[#FA8700] flex items-center px-3 py-1 gap-2">
                        <WarningAmberOutlinedIcon color="white" />
                        <Typography fontSize={16} fontWeight={500} letterSpacing={0.15} color="white.main">
                            <FormattedMessage
                                id="event.guestAccessBanner"
                                defaultMessage="You are currently using special credentials {email} provided by the invitation email."
                                values={{
                                    email: (
                                        <Typography component="span" fontWeight={700}>
                                            ({guestUserInfo?.email})
                                        </Typography>
                                    ),
                                }}
                            />
                        </Typography>
                    </div>
                ) : null}
                {showTemporaryRegistrationBanner ? (
                    <TemporaryRegistrationBanner
                        handleTemporaryRegistrationDialogOpen={handleTemporaryRegistrationDialogOpen}
                    />
                ) : null}
            </>
        ),
        [
            guestUserInfo?.email,
            handleTemporaryRegistrationDialogOpen,
            showGuestAccessBanner,
            showTemporaryRegistrationBanner,
        ]
    );
    usePublicLayoutOptions({ hideChrome: screen === 'credentials', fullWidthTopContent });

    return (
        <PublicEventContext.Provider
            value={{
                event: {
                    data: event,
                    fetching: isEventFetching,
                    loading: isEventLoading,
                },
                oppositeEvent: {
                    data: oppositeEvent,
                    fetching: isOppositeEventFetching,
                    loading: isOppositeEventLoading,
                },
                user: {
                    data: user,
                    loading: isUserLoading,
                },
                guestUserInfo: {
                    data: guestUserInfo,
                    fetching: isGuestUserInfoFetching,
                },
                isLoggedIn,
                toastProps,
                guestAccessHash: guestAccessHashParam,
                register: {
                    fn: registerUser,
                    loading: isRegisterLoading,
                },
                unregister: {
                    fn: unregisterUser,
                    loading: isUnregisterLoading,
                },
                joinWaitingList: {
                    fn: joinWaitingList,
                    loading: isJoiningWaitingList,
                },
                leaveWaitingList: {
                    fn: leaveWaitingList,
                    loading: isUnregisterLoading,
                },
                waitingListSuccess: {
                    state: waitingListActionSuccessDialogState,
                    setState: setWaitingListActionSuccessDialogState,
                },
            }}
        >
            {screen === 'credentials' ? (
                <div className="flex h-screen">
                    <CredentialSelect setShowGuestAccessBanner={setShowGuestAccessBanner} />
                </div>
            ) : (
                <HttpStateWrapper isLoading={isPageLoading} error={eventError}>
                    <EventDetailCardView />
                </HttpStateWrapper>
            )}
            <GenericDialog
                open={waitingListInviteDialogOpen}
                classes={{ paper: 'min-w-[650px]' }}
                titleSX={
                    <div className="flex flex-col gap-5 w-full justify-center">
                        <Typography
                            fontFamily="museo-sans"
                            fontSize={24}
                            letterSpacing={-0.6}
                            textAlign="center"
                            lineHeight="120%"
                            marginBottom={1}
                        >
                            <FormattedMessage
                                id="event.waitingList.invitationTitle"
                                defaultMessage="A seat has become available for the event"
                            />
                        </Typography>
                        <Typography
                            fontSize={16}
                            letterSpacing={0}
                            color="neutral-900.main"
                            textAlign="center"
                            whiteSpace="pre-line"
                        >
                            <FormattedMessage
                                id="event.waitingList.invitationDescription"
                                defaultMessage="You were next on our list! A spot has opened up for {name}.{br}
                                Please let us know if you would like to claim this seat now."
                                values={{ name: event?.name, br: <br /> }}
                            />
                        </Typography>
                    </div>
                }
                primaryBtnProps={{
                    title: intl.formatMessage({ id: 'event.acceptInvitation', defaultMessage: 'Accept Invitation' }),
                    color: 'primary',
                    startIcon: <CheckCircleOutlinedIcon />,
                    loading: isRegisterLoading,
                    onClick: async () => {
                        try {
                            await registerUser({
                                type: eventType,
                                id: Number(eventId),
                                colleagueHash,
                                guestAccessHash: guestAccessHashParam,
                            }).unwrap();
                            setWaitingListInviteDialogOpen(false);
                            setHasRespondedToInvitation(true);
                            setWaitingListSuccessState({
                                title: intl.formatMessage({ id: 'event.registered', defaultMessage: 'Registered' }),
                                description: intl.formatMessage({
                                    id: 'event.registrationSuccess',
                                    defaultMessage: 'You have successfully registered in the event.',
                                }),
                            });
                            setWaitingListSuccessDialogOpen(true);
                        } catch (err) {
                            handleError(
                                intl.formatMessage({
                                    id: 'error.unexpected',
                                    defaultMessage: 'An unexpected error has occurred',
                                }),
                                err
                            );
                            console.error('Failed to accept invitation:', err);
                        }
                    },
                }}
                secondaryBtnProps={{
                    title: intl.formatMessage({ id: 'event.declineInvitation', defaultMessage: 'Decline Invitation' }),
                    color: 'error',
                    startIcon: <CloseIcon />,
                    loading: isLeavingWaitingList,
                    onClick: async () => {
                        try {
                            await leaveWaitingList({
                                type: eventType,
                                id: Number(eventId),
                                colleagueHash,
                                guestAccessHash: guestAccessHashParam,
                            }).unwrap();
                            setWaitingListInviteDialogOpen(false);
                            setHasRespondedToInvitation(true);
                            setWaitingListSuccessState({
                                title: intl.formatMessage({
                                    id: 'event.invitationDeclined',
                                    defaultMessage: 'Invitation Declined',
                                }),
                                description: intl.formatMessage({
                                    id: 'event.invitationDeclinedDescription',
                                    defaultMessage: 'You have declined the invitation to register in this event.',
                                }),
                                icon: <MailOutlinedIcon className="size-10" />,
                            });
                            setWaitingListSuccessDialogOpen(true);
                        } catch (err) {
                            handleError(
                                intl.formatMessage({
                                    id: 'error.unexpected',
                                    defaultMessage: 'An unexpected error has occurred',
                                }),
                                err
                            );
                            console.error('Failed to decline invitation:', err);
                        }
                    },
                }}
                onClose={() => setWaitingListInviteDialogOpen(false)}
            />
            <GenericSuccessDialog
                open={waitingListSuccessDialogOpen}
                onClose={() => setWaitingListSuccessDialogOpen(false)}
                contentProps={{
                    title: waitingListSuccessState?.title,
                    description: waitingListSuccessState?.description || '',
                    icon: waitingListSuccessState?.icon,
                }}
            />
            <GenericSuccessDialog
                open={waitingListActionSuccessDialogState !== 'none'}
                onClose={() => setWaitingListActionSuccessDialogState('none')}
                contentProps={{
                    description:
                        waitingListActionSuccessDialogState === 'join'
                            ? intl.formatMessage({
                                  id: 'event.success.joinWaitingList',
                                  defaultMessage: 'You have successfully joined the waiting list.',
                              })
                            : intl.formatMessage({
                                  id: 'event.success.leaveWaitingList',
                                  defaultMessage: 'You have successfully left the waiting list.',
                              }),
                }}
            />
            <LearnMoreDialog open={temporaryRegistrationDialogOpen} onClose={handleTemporaryRegistrationDialogClose} />
        </PublicEventContext.Provider>
    );
});

export { EventDetail };
